import React, { useState, useCallback } from 'react';
import { useAccount, useWalletClient, useNetwork } from 'wagmi';
import { useToast } from '@/components/ui/use-toast';
import { Tooltip } from '../../components/nft/Tooltip';
import { FilePreview } from '../../components/nft/FilePreview';
import { Progress } from '../../components/nft/Progress';
import { DatePicker } from '../../components/nft/DatePicker';
import { X, Maximize2 } from 'lucide-react';
import { SimpleSEO } from '@/components/seo/SimpleSEO';
import { Logo } from "@/components/layout/Logo";
import { useNFTMinting } from '@/hooks/useNFTMinting';
import { getRarityTier, RARITY_TIERS } from '@/utils/rarityCalculator';
import { NFT_CONTRACT_ADDRESS } from '@/config/nftContract';
import { NFTSuccessCard } from '@/components/nft/NFTSuccessCard';

interface NFTMetadata {
  name: string;
  description: string;
  image: string;
  attributes: Array<{
    trait_type: string;
    value: string;
    rarity?: number;
  }>;
  external_url?: string;
  animation_url?: string;
}

interface CollectionSettings {
  name: string;
  symbol: string;
  description: string;
  coverImage: File | null;
  website: string;
  twitter: string;
  discord: string;
  maxSupply: number;
  royaltyPercentage: number;
  salePrice: string;
  secondarySaleRoyalty: number;
  mintStartDate: Date | null;
  mintEndDate: Date | null;
  isDelayedReveal: boolean;
  isWhitelistEnabled: boolean;
  whitelistAddresses: string[];
}

const NFT_TYPES = [
  { 
    id: 'art', 
    label: 'Art NFT', 
    accepts: 'image/*', 
    description: 'Digital artwork, paintings, illustrations',
    icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z M15 13a3 3 0 11-6 0 3 3 0 016 0z'
  },
  { 
    id: 'music', 
    label: 'Music NFT', 
    accepts: 'audio/*', 
    description: 'Songs, albums, sound clips',
    icon: 'M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'
  },
  { 
    id: 'gaming', 
    label: 'Gaming NFT', 
    accepts: 'image/*', 
    description: 'In-game items, characters, assets',
    icon: 'M14 4.5V14m0-9.5v9.5m0-9.5C14 3.672 13.328 3 12.5 3h-9C2.672 3 2 3.672 2 4.5v9c0 .828.672 1.5 1.5 1.5h9c.828 0 1.5-.672 1.5-1.5v-9zM14 14H2m3.5-4a2.5 2.5 0 100-5 2.5 2.5 0 000 5z'
  },
  { 
    id: 'collectible', 
    label: 'Collectible NFT', 
    accepts: 'image/*', 
    description: 'Trading cards, memorabilia',
    icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
  },
  { 
    id: 'photography', 
    label: 'Photography NFT', 
    accepts: 'image/*', 
    description: 'Professional photographs, landscapes, portraits',
    icon: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z'
  },
  { 
    id: 'sports', 
    label: 'Sports NFT', 
    accepts: 'image/*', 
    description: 'Sports memorabilia, athlete cards, moments',
    icon: 'M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z'
  }
];

const NFTPage: React.FC = () => {
  const { isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const { toast } = useToast();
  const { chain } = useNetwork();
  
  // NFT Minting functionality
  const { 
    isUploading, 
    isMinting, 
    uploadProgress, 
    mintProgress, 
    error: mintError,
    successData,
    mintNFT: executeMintNFT,
    mintBatchNFTs: executeBatchMintNFTs,
    resetState,
    clearSuccessData,
    mintHash,
    batchMintHash
  } = useNFTMinting();
  
  // Get network name based on chain
  const getNetworkName = () => {
    if (chain?.id === 56 || chain?.id === 97) {
      return 'BSCChain';
    }
    if (chain?.id === 1 || chain?.id === 11155111) {
      return 'Ethereum';
    }
    if (chain?.id === 43114 || chain?.id === 43113) {
      return 'Avalanche';
    }
    if (chain?.id === 11124 || chain?.id === 2741) {
      return 'Abstract';
    }
    if (chain?.id === 8453 || chain?.id === 84532) {
      return 'Base';
    }
    if (chain?.id === 10 || chain?.id === 11155420) {
      return 'Optimism';
    }
    if (chain?.id === 42161 || chain?.id === 421614) {
      return 'Arbitrum';
    }
    if (chain?.id === 137 || chain?.id === 80002) {
      return 'Polygon';
    }
    if (chain?.id === 204 || chain?.id === 5611) {
      return 'opBNB';
    }
    // Default to BSCChain
    return 'BSCChain';
  };

  const networkName = getNetworkName();
  
  // Basic NFT state
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [nftDescription, setNftDescription] = useState('');
  const [selectedType, setSelectedType] = useState('art');
  const [file, setFile] = useState<File | null>(null);

  // Collection settings
  const [collectionSettings, setCollectionSettings] = useState<CollectionSettings>({
    name: '',
    symbol: '',
    description: '',
    coverImage: null,
    website: '',
    twitter: '',
    discord: '',
    maxSupply: 10000,
    royaltyPercentage: 10,
    salePrice: '0.1',
    secondarySaleRoyalty: 5,
    mintStartDate: null,
    mintEndDate: null,
    isDelayedReveal: false,
    isWhitelistEnabled: false,
    whitelistAddresses: [],
  });

  // Preview state
  const [showMetadataPreview, setShowMetadataPreview] = useState(false);
  const [previewMetadata, setPreviewMetadata] = useState<NFTMetadata | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Batch upload state
  const [batchFiles, setBatchFiles] = useState<File[]>([]);
  const [isBatchMode, setIsBatchMode] = useState(false);

  // Attributes and rarity state
  const [attributes, setAttributes] = useState<Array<{
    trait_type: string;
    value: string;
    rarity?: number;
    display_type?: 'string' | 'number' | 'boost_number' | 'boost_percentage' | 'date';
  }>>([]);
  const [newAttribute, setNewAttribute] = useState({
    trait_type: '',
    value: '',
    rarity: 0,
    display_type: 'string' as const
  });
  const [showAttributeForm, setShowAttributeForm] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleBatchUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setBatchFiles(files);
  }, []);

  // Attribute management functions
  const addAttribute = () => {
    if (newAttribute.trait_type.trim() && newAttribute.value.trim()) {
      setAttributes(prev => [...prev, {
        trait_type: newAttribute.trait_type.trim(),
        value: newAttribute.value.trim(),
        rarity: newAttribute.rarity || undefined,
        display_type: newAttribute.display_type
      }]);
      setNewAttribute({
        trait_type: '',
        value: '',
        rarity: 0,
        display_type: 'string'
      });
      setShowAttributeForm(false);
    }
  };

  const removeAttribute = (index: number) => {
    setAttributes(prev => prev.filter((_, i) => i !== index));
  };

  const generateMetadataPreview = useCallback(() => {
    const metadata: NFTMetadata = {
      name,
      description: nftDescription,
      image: file ? URL.createObjectURL(file) : '',
      attributes: attributes.map(attr => ({
        trait_type: attr.trait_type,
        value: attr.value,
        rarity: attr.rarity,
        display_type: attr.display_type
      })),
    };
    setPreviewMetadata(metadata);
    setShowMetadataPreview(true);
  }, [name, nftDescription, file, attributes]);

  const handleMint = async () => {
    if (!isConnected) {
      toast({
        title: 'Wallet Not Connected',
        description: 'Please connect your wallet first',
        variant: 'destructive',
      });
      return;
    }

    if (!file && !batchFiles.length) {
      toast({
        title: 'No File Selected',
        description: 'Please select a file to upload',
        variant: 'destructive',
      });
      return;
    }

    if (!name.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please enter an NFT name',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Get the contract address for the current chain
      const contractAddress = NFT_CONTRACT_ADDRESS[chain?.id || 97];
      
      if (!contractAddress) {
        toast({
          title: 'Contract Not Deployed',
          description: `NFT contract is not deployed on ${chain?.name || 'this network'} yet. Please switch to BSC Testnet.`,
          variant: 'destructive',
        });
        return;
      }

      const metadata = {
        name: name.trim(),
        description: nftDescription.trim() || `${name} NFT`,
        attributes: attributes.map(attr => ({
          trait_type: attr.trait_type,
          value: attr.value,
          rarity: attr.rarity,
          display_type: attr.display_type
        }))
      };

      if (isBatchMode && batchFiles.length > 0) {
        await executeBatchMintNFTs({
          files: batchFiles,
          baseMetadata: { ...metadata, baseName: name },
          contractAddress,
          mintPrice: collectionSettings.salePrice
        });
      } else if (file) {
        await executeMintNFT({
          file,
          metadata,
          contractAddress,
          mintPrice: collectionSettings.salePrice
        });
      }

    } catch (error) {
      console.error('NFT minting error:', error);
      toast({
        title: 'Minting Failed',
        description: 'Failed to mint NFT',
        variant: 'destructive',
      });
    }
  };

  const title = `MdbApp ${networkName} NFT Mint`;
  const description = "Create and mint NFTs on multiple blockchain networks. Upload your art, music, gaming assets, and collectibles with custom metadata and collection settings.";
  const keywords = "NFT mint, create NFT, NFT creator, blockchain NFT, crypto art, digital collectibles";

  return (
    <>
      <SimpleSEO
        title={title}
        description={description}
        keywords={keywords}
      />
      <article className="max-w-4xl mx-auto space-y-6 pt-8">
        {/* Page Title */}
        <header className="text-center space-y-4 mb-8">
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            <div className="matrix-logo-left shrink-0">
              <Logo className="w-8 h-8 sm:w-10 sm:h-10" customText="NFT" />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold bg-clip-text text-transparent" style={{
              backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
            }}>
              MdbApp {networkName} NFT Mint
            </h1>
            <div className="matrix-logo-right shrink-0">
              <Logo className="w-8 h-8 sm:w-10 sm:h-10" customText="NFT" />
            </div>
          </div>
          <p className="text-lg sm:text-xl text-muted-foreground">
            Create and mint your NFTs on {networkName} blockchain
          </p>
        </header>

        <div className="w-full p-6">
      {isPreviewOpen && file && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
          style={{ 
            background: 'linear-gradient(to right, var(--color-primary), var(--color-secondary))',
            opacity: 0.95
          }}
          onClick={() => setIsPreviewOpen(false)}
        >
          <div className="relative w-1/2 mx-auto flex justify-center">
            <div className="relative">
              <img
                src={URL.createObjectURL(file)}
                alt="NFT Preview"
                className="max-h-[80vh] w-auto object-contain rounded-lg shadow-xl"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPreviewOpen(false);
                }}
                className="absolute top-4 right-4 text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-6">
        {/* Main NFT Creation Card */}
        <div 
          className="backdrop-blur-sm rounded-lg p-6 border"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderColor: 'var(--color-header-accent)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <h2 
            className="text-2xl font-bold mb-6 bg-clip-text text-transparent"
            style={{
              backgroundImage: 'linear-gradient(to right, var(--color-primary), var(--color-secondary))'
            }}
          >
            Create Your NFT
          </h2>
          
          {/* Batch Mode Toggle */}
          <div className="flex items-center gap-2 mb-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isBatchMode}
                onChange={(e) => setIsBatchMode(e.target.checked)}
                className="rounded focus:ring-2"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-border)',
                  accentColor: 'var(--color-primary)'
                }}
              />
              <span style={{ color: 'var(--color-text)' }}>Batch Upload Mode</span>
            </label>
            <Tooltip content="Upload multiple files at once for batch minting" />
          </div>

          {/* NFT Type Selector */}
          <div className="space-y-6">
            <div>
              <label 
                className="block text-sm font-medium mb-4 flex items-center gap-2"
                style={{ color: 'var(--color-text)' }}
              >
                NFT Type
                <Tooltip content="Select the type of NFT you want to create" />
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {NFT_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    className={`flex flex-col items-center justify-center p-4 rounded-lg border transition-all duration-200 ${
                      selectedType === type.id ? 'text-white' : ''
                    }`}
                    style={{
                      background: selectedType === type.id 
                        ? 'linear-gradient(to right, var(--color-primary), var(--color-secondary))'
                        : 'var(--color-card-bg)',
                      borderColor: selectedType === type.id ? 'transparent' : 'var(--color-border)',
                      color: selectedType === type.id ? '#ffffff' : 'var(--color-text)',
                      opacity: selectedType === type.id ? 1 : 0.7
                    }}
                  >
                    <svg
                      className="w-8 h-8 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d={type.icon}
                      />
                    </svg>
                    <span className="text-sm font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* File Upload with Preview */}
            <div>
              <label 
                className="block text-sm font-medium mb-2 flex items-center gap-2"
                style={{ color: 'var(--color-text)' }}
              >
                Upload {isBatchMode ? 'Files' : 'File'}
                <Tooltip content={`Upload your NFT ${isBatchMode ? 'files' : 'file'} (${NFT_TYPES.find(t => t.id === selectedType)?.description})`} />
              </label>
              <div
                onClick={() => document.getElementById('nft-file-input')?.click()}
                className="w-full px-4 py-2 rounded-lg backdrop-blur-sm cursor-pointer hover:opacity-80 transition-opacity text-center"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-border)',
                  borderWidth: '1px',
                  color: 'var(--color-text)'
                }}
              >
                {isBatchMode ? (
                  batchFiles.length ? `${batchFiles.length} files selected` : 'Click to upload files'
                ) : (
                  file ? file.name : `Click to upload ${NFT_TYPES.find(t => t.id === selectedType)?.label}`
                )}
              </div>
              <input
                id="nft-file-input"
                type="file"
                multiple={isBatchMode}
                className="hidden"
                accept={NFT_TYPES.find(t => t.id === selectedType)?.accepts}
                onChange={isBatchMode ? handleBatchUpload : handleFileChange}
              />
              {file && !isBatchMode && (
                <div className="mt-4 flex justify-center">
                  <div className="relative inline-block">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="NFT Preview"
                      className="max-w-xs rounded-lg shadow-md"
                    />
                    <button
                      onClick={() => setIsPreviewOpen(true)}
                      className="absolute bottom-2 right-2 p-2 rounded-lg text-white"
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.5)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                      }}
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              {isBatchMode && batchFiles.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {batchFiles.map((file, index) => (
                    <FilePreview
                      key={index}
                      file={file}
                      onRemove={() => setBatchFiles(prev => prev.filter((_, i) => i !== index))}
                      className="h-32"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* NFT Basic Information */}
            <div className="space-y-4">
              <h3 
                className="text-lg font-medium"
                style={{ color: 'var(--color-text)' }}
              >
                NFT Information
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--color-text)' }}
                  >
                    NFT Name
                  </label>
                  <input
                    type="url"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter NFT name"
                    className="w-full px-4 py-2 rounded-lg"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: 'var(--color-border)',
                      borderWidth: '1px',
                      color: 'var(--color-text)'
                    }}
                  />
                </div>
                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--color-text)' }}
                  >
                    Symbol
                  </label>
                  <input
                    type="url"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value)}
                    placeholder="Enter symbol"
                    className="w-full px-4 py-2 rounded-lg"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: 'var(--color-border)',
                      borderWidth: '1px',
                      color: 'var(--color-text)'
                    }}
                  />
                </div>
              </div>

              <div>
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-text)' }}
                >
                  Description
                </label>
                <textarea
                  value={nftDescription}
                  onChange={(e) => setNftDescription(e.target.value)}
                  placeholder="Enter NFT description"
                  className="w-full px-4 py-2 rounded-lg h-24 resize-none"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px',
                    color: 'var(--color-text)'
                  }}
                />
              </div>
            </div>

            {/* Attributes & Rarity */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 
                  className="text-lg font-medium"
                  style={{ color: 'var(--color-text)' }}
                >
                  Attributes & Rarity
                </h3>
                <button
                  onClick={() => setShowAttributeForm(true)}
                  className="px-4 py-2 rounded-lg text-white transition-opacity hover:opacity-80"
                  style={{
                    background: 'linear-gradient(to right, var(--color-primary), var(--color-secondary))'
                  }}
                >
                  + Add Attribute
                </button>
              </div>

              {/* Existing Attributes */}
              {attributes.length > 0 && (
                <div className="space-y-2">
                  {attributes.map((attr, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{
                        backgroundColor: 'var(--color-card-bg)',
                        borderColor: 'var(--color-border)',
                        borderWidth: '1px'
                      }}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-4">
                          <span 
                            className="font-medium"
                            style={{ color: 'var(--color-text)' }}
                          >
                            {attr.trait_type}:
                          </span>
                          <span 
                            style={{ color: 'var(--color-text)' }}
                          >
                            {attr.value}
                          </span>
                          {attr.rarity && (
                            <span 
                              className="text-sm px-2 py-1 rounded"
                              style={{ 
                                backgroundColor: getRarityTier(attr.rarity).color,
                                color: 'white'
                              }}
                            >
                              {attr.rarity}% ({getRarityTier(attr.rarity).label})
                            </span>
                          )}
                          <span 
                            className="text-xs px-2 py-1 rounded"
                            style={{ 
                              backgroundColor: 'var(--color-secondary)',
                              color: 'white'
                            }}
                          >
                            {attr.display_type}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeAttribute(index)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Attribute Form */}
              {showAttributeForm && (
                <div 
                  className="p-4 rounded-lg space-y-4"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px'
                  }}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label 
                        className="block text-sm font-medium mb-2"
                        style={{ color: 'var(--color-text)' }}
                      >
                        Trait Type
                      </label>
                      <input
                        type="url"
                        value={newAttribute.trait_type}
                        onChange={(e) => setNewAttribute(prev => ({
                          ...prev,
                          trait_type: e.target.value
                        }))}
                        placeholder="e.g., Background, Eyes, Hat"
                        className="w-full px-3 py-2 rounded-lg"
                        style={{
                          backgroundColor: 'var(--color-card-bg)',
                          borderColor: 'var(--color-border)',
                          borderWidth: '1px',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                    <div>
                      <label 
                        className="block text-sm font-medium mb-2"
                        style={{ color: 'var(--color-text)' }}
                      >
                        Value
                      </label>
                      <input
                        type="url"
                        value={newAttribute.value}
                        onChange={(e) => setNewAttribute(prev => ({
                          ...prev,
                          value: e.target.value
                        }))}
                        placeholder="e.g., Blue, Rare, Golden"
                        className="w-full px-3 py-2 rounded-lg"
                        style={{
                          backgroundColor: 'var(--color-card-bg)',
                          borderColor: 'var(--color-border)',
                          borderWidth: '1px',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label 
                        className="block text-sm font-medium mb-2"
                        style={{ color: 'var(--color-text)' }}
                      >
                        Rarity % (Optional)
                      </label>
                      <input
                        type="url"
                        value={newAttribute.rarity}
                        onChange={(e) => setNewAttribute(prev => ({
                          ...prev,
                          rarity: parseFloat(e.target.value) || 0
                        }))}
                        placeholder="e.g., 5.2"
                        min="0"
                        max="100"
                        step="0.1"
                        className="w-full px-3 py-2 rounded-lg"
                        style={{
                          backgroundColor: 'var(--color-card-bg)',
                          borderColor: 'var(--color-border)',
                          borderWidth: '1px',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                    <div>
                      <label 
                        className="block text-sm font-medium mb-2"
                        style={{ color: 'var(--color-text)' }}
                      >
                        Display Type
                      </label>
                      <select
                        value={newAttribute.display_type}
                        onChange={(e) => setNewAttribute(prev => ({
                          ...prev,
                          display_type: e.target.value as any
                        }))}
                        className="w-full px-3 py-2 rounded-lg"
                        title="Display Type"
                        style={{
                          backgroundColor: 'var(--color-card-bg)',
                          borderColor: 'var(--color-border)',
                          borderWidth: '1px',
                          color: 'var(--color-text)'
                        }}
                      >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="boost_number">Boost Number</option>
                        <option value="boost_percentage">Boost Percentage</option>
                        <option value="date">Date</option>
                      </select>
                    </div>
                  </div>

                  {/* Rarity Guide */}
                  <div 
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: 'var(--color-background)',
                      borderColor: 'var(--color-border)',
                      borderWidth: '1px'
                    }}
                  >
                    <h4 
                      className="text-sm font-medium mb-2"
                      style={{ color: 'var(--color-text)' }}
                    >
                      Rarity Guide:
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                      {Object.entries(RARITY_TIERS).map(([key, tier]) => (
                        <div key={key} className="flex items-center gap-1">
                          <div 
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: tier.color }}
                          />
                          <span style={{ color: 'var(--color-text)' }}>
                            {tier.label} ({tier.min}-{tier.max}%)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={addAttribute}
                      className="px-4 py-2 rounded-lg text-white transition-opacity hover:opacity-80"
                      style={{
                        background: 'linear-gradient(to right, var(--color-primary), var(--color-secondary))'
                      }}
                    >
                      Add Attribute
                    </button>
                    <button
                      onClick={() => setShowAttributeForm(false)}
                      className="px-4 py-2 rounded-lg transition-opacity hover:opacity-80"
                      style={{
                        backgroundColor: 'var(--color-card-bg)',
                        borderColor: 'var(--color-border)',
                        borderWidth: '1px',
                        color: 'var(--color-text)'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Collection Settings */}
            <div className="space-y-4">
              <h3 
                className="text-lg font-medium"
                style={{ color: 'var(--color-text)' }}
              >
                Collection Settings
              </h3>
              
              {/* Basic Settings */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--color-text)' }}
                  >
                    Max Supply
                  </label>
                  <input
                    type="url"
                    value={collectionSettings.maxSupply}
                    onChange={(e) => setCollectionSettings(prev => ({
                      ...prev,
                      maxSupply: parseInt(e.target.value) || 0
                    }))}
                    className="w-full px-4 py-2 rounded-lg"
                    placeholder="Enter max supply"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: 'var(--color-border)',
                      borderWidth: '1px',
                      color: 'var(--color-text)'
                    }}
                  />
                </div>
                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--color-text)' }}
                  >
                    Royalty %
                  </label>
                  <input
                    type="url"
                    value={collectionSettings.royaltyPercentage}
                    onChange={(e) => setCollectionSettings(prev => ({
                      ...prev,
                      royaltyPercentage: parseInt(e.target.value) || 0
                    }))}
                    className="w-full px-4 py-2 rounded-lg"
                    placeholder="Enter royalty percentage"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: 'var(--color-border)',
                      borderWidth: '1px',
                      color: 'var(--color-text)'
                    }}
                  />
                </div>
              </div>

              {/* Mint Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DatePicker
                  label="Mint Start Date"
                  value={collectionSettings.mintStartDate}
                  onChange={(date) => setCollectionSettings(prev => ({
                    ...prev,
                    mintStartDate: date
                  }))}
                  min={new Date()}
                />
                <DatePicker
                  label="Mint End Date"
                  value={collectionSettings.mintEndDate}
                  onChange={(date) => setCollectionSettings(prev => ({
                    ...prev,
                    mintEndDate: date
                  }))}
                  min={collectionSettings.mintStartDate || new Date()}
                />
              </div>

              {/* Advanced Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={collectionSettings.isDelayedReveal}
                      onChange={(e) => setCollectionSettings(prev => ({
                        ...prev,
                        isDelayedReveal: e.target.checked
                      }))}
                      className="rounded focus:ring-2"
                      style={{
                        backgroundColor: 'var(--color-card-bg)',
                        borderColor: 'var(--color-border)',
                        accentColor: 'var(--color-primary)'
                      }}
                    />
                    <span style={{ color: 'var(--color-text)' }}>Delayed Reveal</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={collectionSettings.isWhitelistEnabled}
                      onChange={(e) => setCollectionSettings(prev => ({
                        ...prev,
                        isWhitelistEnabled: e.target.checked
                      }))}
                      className="rounded focus:ring-2"
                      style={{
                        backgroundColor: 'var(--color-card-bg)',
                        borderColor: 'var(--color-border)',
                        accentColor: 'var(--color-primary)'
                      }}
                    />
                    <span style={{ color: 'var(--color-text)' }}>Enable Whitelist</span>
                  </label>
                </div>

                {collectionSettings.isWhitelistEnabled && (
                  <textarea
                    value={collectionSettings.whitelistAddresses.join('\n')}
                    onChange={(e) => setCollectionSettings(prev => ({
                      ...prev,
                      whitelistAddresses: e.target.value.split('\n').filter(Boolean)
                    }))}
                    placeholder="Enter whitelist addresses (one per line)"
                    className="w-full px-4 py-2 rounded-lg h-32 resize-none"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: 'var(--color-border)',
                      borderWidth: '1px',
                      color: 'var(--color-text)'
                    }}
                  />
                )}
              </div>

              {/* Social Links */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <input
                  type="url"
                  value={collectionSettings.website}
                  onChange={(e) => setCollectionSettings(prev => ({
                    ...prev,
                    website: e.target.value
                  }))}
                  placeholder="Website URL"
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px',
                    color: 'var(--color-text)'
                  }}
                />
                <input
                  type="url"
                  value={collectionSettings.twitter}
                  onChange={(e) => setCollectionSettings(prev => ({
                    ...prev,
                    twitter: e.target.value
                  }))}
                  placeholder="Twitter Handle"
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px',
                    color: 'var(--color-text)'
                  }}
                />
                <input
                  type="url"
                  value={collectionSettings.discord}
                  onChange={(e) => setCollectionSettings(prev => ({
                    ...prev,
                    discord: e.target.value
                  }))}
                  placeholder="Discord Invite"
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px',
                    color: 'var(--color-text)'
                  }}
                />
              </div>
            </div>

            {/* Preview Metadata Button */}
            <button
              onClick={generateMetadataPreview}
              className="w-full px-4 py-2 rounded-lg transition-opacity hover:opacity-80"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                color: 'var(--color-text)'
              }}
            >
              Preview Metadata
            </button>

            {/* Mint Progress */}
            {isUploading && (
              <Progress
                value={mintProgress}
                max={100}
                label="Minting Progress"
                className="mt-4"
              />
            )}

            {/* Mint Button */}
            <button
              onClick={handleMint}
              disabled={!isConnected || (!file && !batchFiles.length) || isUploading}
              className="w-full py-3 px-4 rounded-lg font-medium text-white transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              style={{
                background: 'linear-gradient(to right, var(--color-primary), var(--color-secondary))'
              }}
            >
              {isConnected ? (file || batchFiles.length ? 'Mint NFT' : 'Upload File to Mint') : 'Connect Wallet to Mint'}
            </button>
          </div>
        </div>

        {/* Metadata Preview Modal */}
        {showMetadataPreview && previewMetadata && (
          <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div 
              className="rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                opacity: 0.95
              }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 
                  className="text-lg font-medium"
                  style={{ color: 'var(--color-text)' }}
                >
                  Metadata Preview
                </h3>
                <button
                  onClick={() => setShowMetadataPreview(false)}
                  style={{ color: 'var(--color-text)', opacity: 0.7 }}
                  className="hover:opacity-100"
                >
                  <X size={20} />
                </button>
              </div>
              <pre 
                className="p-4 rounded-lg overflow-x-auto"
                style={{
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text)'
                }}
              >
                {JSON.stringify(previewMetadata, null, 2)}
              </pre>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Success Card */}
      {(mintHash?.hash || batchMintHash?.hash) && (
        <NFTSuccessCard
          data={{
            transactionHash: mintHash?.hash || batchMintHash?.hash || '',
            contractAddress: NFT_CONTRACT_ADDRESS[chain?.id || 97] || '',
            imageUrl: file ? URL.createObjectURL(file) : '',
            metadataUrl: '', // Will be populated from IPFS
            name: name,
            description: nftDescription,
            mintPrice: `${collectionSettings.salePrice} ${chain?.nativeCurrency?.symbol || 'BNB'}`,
            platformFee: `${(parseFloat(collectionSettings.salePrice) * 0.025).toFixed(4)} ${chain?.nativeCurrency?.symbol || 'BNB'}`,
            chainId: chain?.id || 97,
            explorerUrl: chain?.id === 56 ? 'https://bscscan.com' :
                        chain?.id === 97 ? 'https://testnet.bscscan.com' :
                        chain?.id === 1 ? 'https://etherscan.io' :
                        'https://testnet.bscscan.com'
          }}
          onClose={() => {
            // Reset the form
            setName('');
            setNftDescription('');
            setFile(null);
            setAttributes([]);
          }}
          onMintAnother={() => {
            // Just close and keep the form ready
            setFile(null);
          }}
        />
      )}
      </article>
    </>
  );
};

export default NFTPage;
